<template>
  <div class="wizzard__summary">
    <WizzardSummaryItem
      v-for="item in items"
      :key="item.title"
      :title="item.title"
      :value="item.value"
    />
    <WizzardSummaryItem title="Description" :value="transactionNotes" />
    <WizzardSummaryItem
      v-for="item in additionalItems"
      :key="item.title"
      v-bind="item"
    />
    <WizzardSummaryItem
      v-if="prices?.length"
      :title="valueLabel"
      :value="valuation"
    />
    <WizzardSummaryItem
      v-for="(tag, index) in displayTags"
      :key="index"
      :title="tag[0]"
      :value="tag[1]"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, inject } from 'vue'

import { AssetClass, AssetPriceForm, TransactionClass } from '@types'

import {
  ASSET_FIELD,
  TRANSACTION_FIELD,
} from '@/const'

import { numberFormat } from '@/helpers/numbers'

import { useAssetsBunchStore } from '@/store/assets/bunch'

import WizzardSummaryItem from '../components/WizzardSummaryItem.vue'

type Props = {
  useExistingAccount: boolean
  transaction: TransactionClass
  account: AssetClass
  asset: AssetClass
  prices?: AssetPriceForm[]
  tags: Record<string, string>
}

const props = defineProps<Props>()

const assetsBunchStore = useAssetsBunchStore()

const currency = inject<ComputedRef<string>>('currency')
const accountLabel = inject<string>('accountLabel', 'Account')
const assetLabel = inject<string>('assetLabel', 'Asset')
const dateLabel = inject<string>('assetDateLabel', 'Date')
const amountLabel = inject<string>('amountLabel', 'Amount')
const valueLabel = inject<string>('valueLabel', 'Value')
const summary = inject<
  [
    'account' | 'asset' | 'asset-name' | 'date' | 'amount' | 'description',
    string,
  ][]
>('summaryData', [
  ['account', accountLabel],
  ['asset', assetLabel],
  ['date', dateLabel],
  ['amount', amountLabel],
])
const additionalItems = inject<
  | ComputedRef<
      {
        title: string
        value: string
      }[]
    >
  | undefined
>('additionalSummary', undefined)

const items = computed(() => {
  return summary.map(item => {
    let value
    switch (item[0]) {
      case 'account':
        value = accountData.value
        break
      case 'asset':
        value = assetData.value
        break
      case 'asset-name':
        value = assetName.value
        break
      case 'description':
        value = description.value
        break
      case 'date':
        value = date.value
        break
      case 'amount':
        value = amount.value
        break
    }
    return {
      title: item[1],
      value,
    }
  })
})

const date = computed(
  () => props.transaction.field<string>(TRANSACTION_FIELD.DATE).value,
)

const accountData = computed(() => {
  let account
  if (props.useExistingAccount) {
    const accountId = props.transaction.entry1().value?.account_id
    if (!accountId) return
    account = assetsBunchStore.getElementById(accountId)
  } else {
    account = props.account
  }
  const result = [account?.field<string>(ASSET_FIELD.NAME)?.value]
  if (account?.field<string>(ASSET_FIELD.TYPE)?.value) {
    result.push(` (${account.field<string>(ASSET_FIELD.TYPE)?.value})`)
  }
  return result.join('')
})

const assetName = computed(() => {
  return props.asset.field<string>(ASSET_FIELD.NAME)?.value
})

const assetData = computed(() => {
  const result = [assetName.value]
  if (props.asset.field<string>(ASSET_FIELD.TYPE)?.value) {
    result.push(` (${props.asset.field<string>(ASSET_FIELD.TYPE)?.value})`)
  }
  return result.join('')
})

const amount = computed(() => {
  const amount = props.transaction.entry2().value?.amount
  return amount
    ? numberFormat(Math.abs(amount), { currency: currency?.value })
    : undefined
})

const description = computed(
  () => props.asset.field<string>(ASSET_FIELD.DESCRIPTION)?.value,
)

const transactionNotes = computed(
  () => props.transaction.field<string>(TRANSACTION_FIELD.DESCRIPTION)?.value,
)

const valuation = computed(() => {
  const count = props.prices?.length
  return count ? `${count} ${count === 1 ? 'item' : 'items'}` : undefined
})

const displayTags = computed(() => Object.entries(props.tags))
</script>
